// src/pages/admin/AdminPaymentsManagement.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import API from '../../services/api';
import { toast } from 'react-toastify';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

const AdminPaymentsManagement = () => {
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const safeLower = (v) => String(v ?? '').toLowerCase();
  const safeStr = (v) => String(v ?? '').trim();

  const normalize = (v) =>
    safeStr(v)
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .replace(/[^\p{L}\p{N}\s]/gu, '');

  const fetchPayments = useCallback(async () => {
    if (mountedRef.current) setLoading(true);
    try {
      const res = await API.get('/admin/payments');
      const list = res?.data?.success ? res.data.data || [] : [];
      if (mountedRef.current) setPayments(Array.isArray(list) ? list : []);
    } catch (e) {
      toast.error('Erreur chargement paiements');
      if (mountedRef.current) setPayments([]);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await API.get('/admin/payments/stats');
      if (res?.data?.success && res.data.data && mountedRef.current) {
        setStats(res.data.data);
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    fetchPayments();
    fetchStats();
  }, [fetchPayments, fetchStats]);

  // 🔑 provider = cmi | payzone
  const getProvider = (p) => safeLower(p?.provider || p?.paymentProvider || '');

  const getRawMethod = (p) =>
    safeLower(p?.method || p?.paymentMethod || p?.orderId?.paymentMethod).trim();

  const classifyMethod = (p) => {
    const key = getRawMethod(p);
    const provider = getProvider(p);

    const isCmi = provider === 'cmi';

    const isCard =
      key === 'card' ||
      key === 'carte' ||
      key.includes('visa') ||
      key.includes('master') ||
      key.includes('card') ||
      provider === 'payzone'; // ✅ Payzone = carte bancaire

    const isCash =
      key === 'cash' ||
      key === 'espece' ||
      key === 'espèce' ||
      key === 'especes' ||
      key === 'espèces';

    return { key, isCmi, isCard, isCash, provider };
  };

  const getUserName = (p) => {
    const u = p?.userId || {};
    const full = `${u?.firstName || ''} ${u?.lastName || ''}`.trim();
    return full || u?.name || u?.email || 'Client';
  };

  const getCmiName = (p) =>
    safeStr(
      p?.cmiResponse?.BillToName ||
        p?.paymentData?.BillToName ||
        p?.cmiResponse?.billToName ||
        p?.paymentData?.billToName ||
        ''
    );

  const getCmiEmail = (p) =>
    safeStr(
      p?.cmiResponse?.email ||
        p?.paymentData?.email ||
        p?.cmiResponse?.Email ||
        p?.paymentData?.Email ||
        ''
    );

  const getCmiPhone = (p) =>
    safeStr(
      p?.cmiResponse?.tel ||
        p?.cmiResponse?.phone ||
        p?.paymentData?.tel ||
        p?.paymentData?.phone ||
        p?.cmiResponse?.BillToTel ||
        p?.paymentData?.BillToTel ||
        ''
    );

  const isDifferentName = (maxulaName, cmiName) => {
    const a = normalize(maxulaName);
    const b = normalize(cmiName);
    if (!a || !b) return false;
    if (a.includes(b) || b.includes(a)) return false;
    return a !== b;
  };

  const getStatusBadge = (statusRaw) => {
    const status = safeLower(statusRaw).trim();
    const styles = {
      completed: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-purple-100 text-purple-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      completed: 'Complété',
      pending: 'En attente',
      failed: 'Échoué',
      refunded: 'Remboursé',
      cancelled: 'Annulé'
    };
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-semibold ${
          styles[status] || 'bg-gray-100 text-gray-800'
        }`}
      >
        {labels[status] || status || 'Inconnu'}
      </span>
    );
  };

  const getMethodLabel = (p) => {
    const { isCmi, isCard, isCash, provider, key } = classifyMethod(p);
    if (isCmi) return 'CMI';
    if (provider === 'payzone') return 'Payzone (Carte bancaire)';
    if (isCard) return 'Carte bancaire';
    if (isCash) return 'Espèces';
    return key || 'Inconnu';
  };

  const filteredPayments = useMemo(() => {
    const term = safeLower(searchTerm).trim();

    return (payments || []).filter((p) => {
      const tx = safeLower(p?.transactionId);
      const ord = safeLower(p?.orderNumber || p?.orderId?.orderNumber);
      const email = safeLower(p?.userId?.email);
      const phone = safeLower(p?.userId?.phone);
      const name = safeLower(getUserName(p));

      const cmiName = safeLower(getCmiName(p));
      const cmiEmail = safeLower(getCmiEmail(p));
      const cmiPhone = safeLower(getCmiPhone(p));

      const status = safeLower(p?.status).trim();
      const { isCmi, isCard, isCash } = classifyMethod(p);

      const matchesSearch =
        !term ||
        tx.includes(term) ||
        ord.includes(term) ||
        email.includes(term) ||
        phone.includes(term) ||
        name.includes(term) ||
        cmiName.includes(term) ||
        cmiEmail.includes(term) ||
        cmiPhone.includes(term);

      const matchesStatus = statusFilter === 'all' || status === statusFilter;

      let matchesMethod = true;
      if (methodFilter === 'cmi') matchesMethod = isCmi;
      else if (methodFilter === 'card') matchesMethod = isCard;
      else if (methodFilter === 'cash') matchesMethod = isCash;

      return matchesSearch && matchesStatus && matchesMethod;
    });
  }, [payments, searchTerm, statusFilter, methodFilter]);

  const totalItems = filteredPayments.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / limit));
  const startIndex = (page - 1) * limit;
  const paginatedPayments = filteredPayments.slice(
    startIndex,
    startIndex + limit
  );

  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter, methodFilter, limit]);

  const goToPage = (p) => {
    const next = Math.max(1, Math.min(totalPages, p));
    if (next !== page) setPage(next);
  };

  const displayStats = stats || {
    revenue: { total: 0, month: 0, year: 0 },
    counts: { completed: 0, pending: 0, failed: 0, refunded: 0, total: 0 }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin h-10 w-10 border-b-2 border-primary-600 rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Gestion des Paiements</h1>
        <p className="text-gray-600 mt-1">Suivez toutes les transactions</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Stat
          label="Total Reçu"
          value={`${Number(displayStats.revenue.total || 0).toLocaleString()} MAD`}
          color="green"
        />
        <Stat
          label="Ce Mois"
          value={`${Number(displayStats.revenue.month || 0).toLocaleString()} MAD`}
          color="blue"
        />
        <Stat
          label="Complétés"
          value={Number(displayStats.counts.completed || 0)}
          color="green"
        />
        <Stat
          label="En Attente"
          value={Number(displayStats.counts.pending || 0)}
          color="yellow"
        />
      </div>

      <div className="bg-white p-4 rounded shadow mb-6 grid md:grid-cols-4 gap-4">
        <input
          className="border rounded px-3 py-2 md:col-span-2"
          placeholder="Transaction, email, nom, téléphone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <select
          className="border rounded px-3 py-2"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">Tous les statuts</option>
          <option value="completed">Complétés</option>
          <option value="pending">En attente</option>
          <option value="failed">Échoués</option>
          <option value="refunded">Remboursés</option>
        </select>

        <select
          className="border rounded px-3 py-2"
          value={methodFilter}
          onChange={(e) => setMethodFilter(e.target.value)}
        >
          <option value="all">Toutes les méthodes</option>
          <option value="cmi">CMI</option>
          <option value="card">Carte bancaire</option>
          <option value="cash">Espèces</option>
        </select>
      </div>

      <div className="bg-white rounded shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 text-xs uppercase">
            <tr>
              <th className="p-3 text-left">Transaction</th>
              <th className="p-3 text-left">Commande</th>
              <th className="p-3 text-left">Client</th>
              <th className="p-3 text-left">Montant</th>
              <th className="p-3 text-left">Méthode</th>
              <th className="p-3 text-left">Statut</th>
              <th className="p-3 text-left">Date</th>
            </tr>
          </thead>
          <tbody>
            {paginatedPayments.length > 0 ? (
              paginatedPayments.map((p) => {
                const txId =
                  p?.transactionId || (p?._id ? String(p._id).slice(-8) : 'N/A');
                const orderNumber =
                  p?.orderNumber || p?.orderId?.orderNumber || '-';
                const maxulaName = getUserName(p);
                const maxulaEmail = p?.userId?.email || '';
                const maxulaPhone = p?.userId?.phone || '';

                const { isCmi } = classifyMethod(p);

                const cmiName = getCmiName(p);
                const cmiEmail = getCmiEmail(p);
                const cmiPhone = getCmiPhone(p);

                const diffName =
                  isCmi && cmiName && isDifferentName(maxulaName, cmiName);

                const created = p?.createdAt ? new Date(p.createdAt) : null;

                return (
                  <tr key={p._id || txId} className="border-t hover:bg-gray-50">
                    <td className="p-3 font-mono text-sm">#{txId}</td>
                    <td className="p-3 text-sm text-gray-700">{orderNumber}</td>
                    <td className="p-3 text-sm text-gray-700">
                      <div className="font-semibold text-gray-900">
                        {maxulaName}
                      </div>
                      <div className="text-xs text-gray-500">
                        {maxulaEmail || 'N/A'}
                        {maxulaPhone ? ` • ${maxulaPhone}` : ''}
                      </div>

                      {isCmi && (cmiName || cmiEmail || cmiPhone) && (
                        <div className="mt-2 bg-gray-50 border rounded p-2 text-xs">
                          <div className="font-semibold flex items-center gap-2">
                            <span>Infos CMI</span>
                            {diffName && (
                              <span className="bg-orange-100 text-orange-800 px-2 py-0.5 rounded-full font-semibold">
                                Nom carte ≠ compte
                              </span>
                            )}
                          </div>
                          {cmiName && (
                            <div className="mt-1">
                              Nom carte:{' '}
                              <span className="font-medium">{cmiName}</span>
                            </div>
                          )}
                          {cmiEmail && (
                            <div className="mt-1">
                              Email CMI:{' '}
                              <span className="font-medium">{cmiEmail}</span>
                            </div>
                          )}
                          {cmiPhone && (
                            <div className="mt-1">
                              Tél CMI:{' '}
                              <span className="font-medium">{cmiPhone}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-sm font-semibold text-gray-900">
                      {(Number(p?.amount) || 0).toFixed(2)} MAD
                    </td>
                    <td className="p-3 text-sm text-gray-700">
                      {getMethodLabel(p)}
                    </td>
                    <td className="p-3">{getStatusBadge(p?.status)}</td>
                    <td className="p-3 text-sm text-gray-500">
                      {created ? created.toLocaleDateString('fr-FR') : '-'}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="7" className="p-10 text-center text-gray-500">
                  Aucun paiement trouvé
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 border-t">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => goToPage(page - 1)}
              disabled={page <= 1}
              className="border px-3 py-2 rounded disabled:opacity-50 flex items-center gap-1"
            >
              <FiChevronLeft /> <span>Préc</span>
            </button>
            <button
              type="button"
              onClick={() => goToPage(page + 1)}
              disabled={page >= totalPages}
              className="border px-3 py-2 rounded disabled:opacity-50 flex items-center gap-1"
            >
              <span>Suiv</span> <FiChevronRight />
            </button>
            <span className="text-sm text-gray-600 ml-2">
              Page <strong>{page}</strong> / <strong>{totalPages}</strong>
            </span>
            <div className="text-sm text-gray-600 ml-4">
              <span className="mr-2">Afficher</span>
              <select
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                className="border px-2 py-1 rounded"
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <span className="ml-2">/ page</span>
            </div>
          </div>
          <div className="text-sm text-gray-600">
            Total: <strong>{totalItems}</strong> résultat(s)
          </div>
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value, color = 'green' }) => {
  const colorClass =
    {
      green: 'text-green-600',
      blue: 'text-blue-600',
      yellow: 'text-yellow-600',
      red: 'text-red-600',
      purple: 'text-purple-600',
      gray: 'text-gray-700'
    }[color] || 'text-gray-700';

  return (
    <div className="bg-white rounded shadow p-4">
      <p className="text-sm text-gray-500">{label}</p>
      <p className={`text-2xl font-bold ${colorClass}`}>{value}</p>
    </div>
  );
};

export default AdminPaymentsManagement;
